// src/pages/ekd/EkdReview.tsx — EKD SME review as a DECISION QUEUE.
// Structure · Reference data (governed read-only + delta) · Candidate rules · Context (read-only).
import React, { useCallback, useEffect, useState } from "react";
import { ekd_getSummary, ekd_getLane, ekd_review, ekd_refreshReference,
         type EkdSummary, type LaneResponse, type LaneKey, type ReviewItem } from "../api/ekdAPI";

const LANES: { key: LaneKey; label: string }[] = [
  { key: "structure", label: "Structure" },
  { key: "reference", label: "Reference data" },
  { key: "rules", label: "Candidate rules" },
  { key: "context", label: "Context" },
];
const GROUP_LABEL: Record<string, string> = {
  grains: "Grains", relationships: "Relationships",
  governed_readonly: "Governed concepts (from lookup)", pending_lookup: "Pending in lookup", needs_routing: "Needs routing",
  agent_confident: "Agent-confident (quick confirm)", needs_your_input: "Needs your input", missing_policies: "Missing policies", roles: "Unresolved roles",
  fields: "Fields", business_terms: "Business terms",
};
type Filter = "pending" | "confirmed" | "flagged" | "all";

export default function EkdReview({ entityId, entityLabel }: { entityId: string; entityLabel?: string }) {
  const [summary, setSummary] = useState<EkdSummary | null>(null);
  const [lane, setLane] = useState<LaneKey>("structure");
  const [data, setData] = useState<LaneResponse | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("pending");
  const [q, setQ] = useState(""); const [busy, setBusy] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ id: string; value: string } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2200); };

  const loadSummary = useCallback(async () => { try { setSummary(await ekd_getSummary(entityId)); } catch { setSummary(null); } }, [entityId]);
  const loadLane = useCallback(async (l: LaneKey) => {
    try { const r = await ekd_getLane(entityId, l); setData(r); setGroup((g) => (g && r.groups[g]) ? g : Object.keys(r.groups)[0]); }
    catch { setData(null); }
  }, [entityId]);
  useEffect(() => { loadSummary(); }, [entityId]);       // eslint-disable-line
  useEffect(() => { loadLane(lane); }, [lane, entityId]); // eslint-disable-line

  const act = async (it: ReviewItem, status: string, value: any = null) => {
    setBusy(it.id);
    try { const r = await ekd_review(entityId, it.type, it.id, status, value); setData(r); setEdit(null);
          loadSummary(); note(status === "confirmed" ? "Done." : status === "flagged" ? "Recorded." : "Reopened."); }
    catch { note("Failed"); } finally { setBusy(null); }
  };

  const groups = data?.groups || {};
  const ro = new Set(data?.readonly_groups || []);
  const isRO = (g: string) => ro.has(g);
  const items: ReviewItem[] = (group && groups[group]) || [];
  const shown = items.filter((it) =>
    (isRO(group || "") || filter === "all" || it.status === filter) &&
    (!q || it.title.toLowerCase().includes(q.toLowerCase()) || (it.subtitle || "").toLowerCase().includes(q.toLowerCase())));

  const refreshFromLookup = async () => {
    setBusy("refresh");
    try { const r = await ekd_refreshReference(entityId); note(`Reference refreshed: ${r.reference_domains} concepts, ${r.values} values. BC/DQ will reflect it.`); await loadSummary(); await loadLane(lane); }
    catch { note("Refresh failed"); } finally { setBusy(null); }
  };
  const rd = summary?.readiness;
  const pct = (n?: number) => (n == null ? "—" : Math.round(n * 100) + "%");

  return (
    <div className="ek">
      <style>{CSS}</style>
      <div className="ek-top">
        <div><b>Enterprise Knowledge · SME decisions</b><small> {entityLabel || summary?.entity || entityId}{summary?.domain ? " · " + summary.domain : ""}</small></div>
        <div className="sp" /><button className="ek-btn" onClick={() => { loadSummary(); loadLane(lane); }}>Refresh</button>
      </div>
      {flash && <div className="ek-flash">{flash}</div>}

      {rd && summary && <div className="ek-ready">
        <div className="ek-meter"><div className="ek-meter-fill" style={{ width: `${(rd.sme_decided_share || 0) * 100}%` }} /></div>
        <div className="ek-ready-lbls">
          <span><b>{summary.totals.confirmed}</b>/{summary.totals.decisions} decisions made ({pct(rd.sme_decided_share)})</span>
          <span>business readiness <b>{pct(rd.baseline_business_readiness)}</b></span>
          <span className="ek-muted">{summary.governed_reference_domains} reference concepts governed via lookup (Ready){summary.pending_reference_domains ? `, ${summary.pending_reference_domains} pending Step 1/2` : ""}</span>
        </div>
      </div>}

      <div className="ek-lanes">
        {LANES.map((L) => {
          const c = summary?.lanes[L.key];
          return <button key={L.key} className={"ek-lane " + (lane === L.key ? "on" : "")} onClick={() => setLane(L.key)}>
            {L.label}{c && c.total > 0 && <span className="ek-lane-n">{c.pending}</span>}</button>;
        })}
      </div>

      <div className="ek-bar">
        <div className="ek-groups">
          {Object.keys(groups).map((g) => (
            <button key={g} className={"ek-gp " + (group === g ? "on" : "") + (isRO(g) ? " ro" : "")} onClick={() => setGroup(g)}>
              {GROUP_LABEL[g] || g}<span className="ek-gp-n">{groups[g].length}</span></button>))}
        </div>
        <div className="sp" />
        {lane === "reference" && <button className="ek-btn" disabled={busy === "refresh"} onClick={refreshFromLookup} title="Re-project reference domains from the lookup store so BC/DQ pick up your changes">{busy === "refresh" ? "Refreshing…" : "↻ Refresh from lookup"}</button>}
        {group && !isRO(group) && <div className="ek-filters">{(["pending", "confirmed", "flagged", "all"] as Filter[]).map((f) =>
          <button key={f} className={"ek-fp " + (filter === f ? "on" : "")} onClick={() => setFilter(f)}>{f}</button>)}</div>}
        <input className="ek-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="ek-list">
        {shown.map((it) => {
          const readOnly = isRO(group || "") || it.readonly;
          return (
            <div key={it.id} className={"ek-item s-" + it.status + (readOnly ? " ro" : "")}>
              <div className="ek-item-main">
                <div className="ek-item-t">{it.title}
                  {it.badge && <span className="ek-tag hint">{it.badge}</span>}
                  {!readOnly && it.status === "confirmed" && <span className="ek-tag ok">✓ done</span>}
                  {!readOnly && it.status === "flagged" && <span className="ek-tag no">rejected</span>}
                  {it.empty && <span className="ek-tag warn">no values</span>}
                </div>
                {it.subtitle && <div className="ek-item-s">{it.subtitle}</div>}
                {it.detail && <div className="ek-item-d">{it.detail}</div>}
                {it.question && <div className="ek-item-q">❓ {it.question}</div>}
                {(it as any).evidence && <div className="ek-item-e">🔎 {(it as any).evidence}</div>}
                {it.why && !readOnly && <div className="ek-item-w">→ {it.why}</div>}
                {edit?.id === it.id && (
                  <div className="ek-edit">
                    <textarea value={edit.value} onChange={(e) => setEdit({ id: it.id, value: e.target.value })}
                      placeholder={it.gap_type === "policy" ? "Policy (allowed values / range / mandatory)…" : it.gap_type === "role" ? "Role…" : "Value…"} />
                    <button className="ek-btn ok" disabled={busy === it.id} onClick={() => act(it, "confirmed", edit.value)}>{it.confirm_label || "Save"}</button>
                    <button className="ek-btn" onClick={() => setEdit(null)}>Cancel</button>
                  </div>)}
              </div>
              {!readOnly && (
                <div className="ek-item-acts">
                  {it.editable
                    ? (edit?.id !== it.id && it.status !== "confirmed" && <button className="ek-mini ok" onClick={() => setEdit({ id: it.id, value: typeof it.value === "string" ? it.value : "" })}>{it.confirm_label || "Answer"}</button>)
                    : (it.status !== "confirmed" && <button className="ek-mini ok" disabled={busy === it.id} onClick={() => act(it, "confirmed")}>{it.confirm_label || "Confirm"}</button>)}
                  {it.lockable && it.status !== "confirmed" && <button className="ek-mini" disabled={busy === it.id} onClick={() => act(it, "confirmed", "locked")}>Confirm & lock</button>}
                  {it.status !== "flagged" && <button className="ek-mini no" disabled={busy === it.id} onClick={() => act(it, "flagged")}>{it.flag_label || "Flag"}</button>}
                  {it.status !== "pending" && <button className="ek-mini" disabled={busy === it.id} onClick={() => act(it, "pending")}>Reopen</button>}
                </div>)}
            </div>);
        })}
        {shown.length === 0 && <div className="ek-empty">Nothing here.</div>}
      </div>
    </div>
  );
}

const CSS = `
.ek{--ink:#16306e;--line:#d9e0ea;--tx:#0f1923;--tx2:#5b6b7c;--gov:#166534;--gov-bg:#e9f6ee;--warn:#7c2d00;--warn-bg:#fff7ed;--no:#991b1b;--no-bg:#fef2f2;--sf:#fff;--sf2:#f5f8fc;font-family:'Sora',system-ui,sans-serif;color:var(--tx)}
.ek .sp{flex:1}.ek-muted{color:var(--tx2)}
.ek-top{display:flex;align-items:center;padding:10px 2px}.ek-top small{color:var(--tx2)}
.ek-flash{background:var(--gov-bg);color:var(--gov);padding:6px 12px;border-radius:6px;font-size:12.5px;margin-bottom:8px}
.ek-ready{background:var(--sf);border:1px solid var(--line);border-radius:10px;padding:10px 14px;margin-bottom:10px}
.ek-meter{height:8px;background:var(--sf2);border-radius:20px;overflow:hidden}.ek-meter-fill{height:100%;background:linear-gradient(90deg,#1e7a46,#166534);border-radius:20px;transition:width .3s}
.ek-ready-lbls{display:flex;gap:18px;flex-wrap:wrap;font-size:12px;color:var(--tx2);margin-top:7px}.ek-ready-lbls b{color:var(--tx)}
.ek-lanes{display:flex;gap:4px;border-bottom:1px solid var(--line);margin-bottom:10px}
.ek-lane{border:none;background:none;padding:9px 16px;font-size:13.5px;cursor:pointer;color:var(--tx2);font-family:inherit;border-bottom:2px solid transparent;display:inline-flex;align-items:center;gap:7px}
.ek-lane.on{color:var(--ink);font-weight:600;border-bottom-color:var(--gov)}
.ek-lane-n{font-size:11px;background:var(--warn-bg);color:var(--warn);border-radius:20px;padding:0 7px;font-weight:600}
.ek-bar{display:flex;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap}
.ek-groups{display:inline-flex;background:var(--sf2);border:1px solid var(--line);border-radius:9px;padding:3px;gap:2px;flex-wrap:wrap}
.ek-gp{border:none;background:none;padding:5px 11px;border-radius:7px;font-size:12.5px;cursor:pointer;color:var(--tx2);font-family:inherit;display:inline-flex;gap:6px;align-items:center}
.ek-gp.on{background:var(--sf);box-shadow:0 1px 2px rgba(16,32,43,.1);font-weight:600;color:var(--ink)}.ek-gp.ro{font-style:italic}
.ek-gp-n{font-size:10.5px;color:var(--tx2)}
.ek-filters{display:flex;gap:4px}.ek-fp{border:1px solid var(--line);background:var(--sf);border-radius:20px;padding:4px 11px;font-size:11.5px;cursor:pointer;color:var(--tx2);font-family:inherit;text-transform:capitalize}.ek-fp.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.ek-search{border:1px solid var(--line);border-radius:7px;padding:6px 10px;font-size:12.5px;font-family:inherit;min-width:150px}
.ek-list{display:flex;flex-direction:column;gap:8px;max-height:640px;overflow:auto;padding-right:4px}
.ek-item{display:flex;gap:12px;border:1px solid var(--line);border-left:3px solid var(--line);border-radius:9px;padding:11px 13px;background:var(--sf)}
.ek-item.s-confirmed{border-left-color:var(--gov);background:#fbfefc}.ek-item.s-flagged{border-left-color:var(--no);background:#fffbfb}
.ek-item.ro{border-left-color:var(--line);background:var(--sf2);opacity:.92}
.ek-item-main{flex:1;min-width:0}
.ek-item-t{font-weight:600;font-size:13.5px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.ek-item-s{font-size:11.5px;color:var(--tx2);margin-top:2px;font-family:'JetBrains Mono',monospace}
.ek-item-d{font-size:12px;color:#44515f;margin-top:5px;line-height:1.4}
.ek-item-q{font-size:12px;color:#7c5310;background:var(--warn-bg);border-radius:6px;padding:5px 9px;margin-top:6px}
.ek-item-w{font-size:11.5px;color:var(--gov);margin-top:5px;font-weight:500}
.ek-item-e{font-size:11.5px;color:#44515f;margin-top:5px;background:var(--sf2);border-radius:6px;padding:5px 9px}
.ek-tag{font-size:10px;font-weight:700;padding:1px 7px;border-radius:10px}.ek-tag.ok{background:var(--gov-bg);color:var(--gov)}.ek-tag.no{background:var(--no-bg);color:var(--no)}.ek-tag.warn{background:var(--warn-bg);color:var(--warn)}.ek-tag.hint{background:var(--gov-bg);color:var(--gov)}
.ek-edit{margin-top:8px;display:flex;gap:6px;align-items:flex-start;flex-wrap:wrap}.ek-edit textarea{flex:1;min-width:240px;min-height:52px;border:1px solid var(--line);border-radius:7px;padding:7px 9px;font-family:inherit;font-size:12.5px}
.ek-item-acts{display:flex;flex-direction:column;gap:5px;flex-shrink:0}
.ek-mini{border:1px solid var(--line);background:var(--sf);border-radius:6px;padding:4px 11px;font-size:11.5px;cursor:pointer;font-family:inherit;color:var(--tx);white-space:nowrap}
.ek-mini.ok{background:var(--gov);color:#fff;border-color:var(--gov)}.ek-mini.no{color:var(--no);border-color:#e6c3c3}
.ek-btn{border:1px solid var(--line);background:var(--sf);border-radius:7px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;color:var(--tx)}.ek-btn.ok{background:var(--gov);color:#fff;border-color:var(--gov)}
.ek-empty{color:var(--tx2);font-size:12.5px;padding:24px;text-align:center}
`;
